import React from 'react';
import LegalLayout from './LegalLayout';

export default function AIPolicy() {
    return (
        <LegalLayout title="AI Policy" lastUpdated="April 3, 2026">
            <p>
                NurtureCRM.ai incorporates artificial intelligence features to help sales teams work more effectively. This AI Policy explains how we use AI within the Service, what data is processed, and the safeguards we have in place.
            </p>

            <h2>1. AI Features Overview</h2>
            <p>NurtureCRM.ai uses AI to provide the following capabilities:</p>
            <ul>
                <li><strong>AI Sales Assistant:</strong> Natural language conversational interface that helps you query your CRM data, draft emails, summarize activities, and get sales insights.</li>
                <li><strong>AI Heartbeat:</strong> Automated monitoring of your pipeline that identifies at-risk deals, stale contacts, and suggests follow-up actions.</li>
                <li><strong>Email Composition:</strong> AI-assisted email drafting, reply suggestions, and tone analysis for sales communications.</li>
                <li><strong>Contact and Deal Insights:</strong> AI-generated summaries, relationship scoring, and next-step recommendations based on CRM activity data.</li>
                <li><strong>Voice Profiles:</strong> Customizable AI writing profiles that match your communication style.</li>
                <li><strong>Smart Suggestions:</strong> Contextual recommendations for deal stages, activities, and engagement strategies.</li>
            </ul>

            <h2>2. How AI Processes Your Data</h2>

            <h3>2.1 Data Inputs</h3>
            <p>AI features may process the following data from your CRM:</p>
            <ul>
                <li>Contact and account information (names, companies, roles)</li>
                <li>Deal details (stage, value, probability, notes)</li>
                <li>Activity history (emails, calls, meetings, tasks)</li>
                <li>Email content (for drafting, summarizing, and reply suggestions)</li>
                <li>User queries and prompts (when interacting with the AI assistant)</li>
            </ul>

            <h3>2.2 Data Processing</h3>
            <ul>
                <li>AI processing is performed using third-party large language model (LLM) providers.</li>
                <li>Data sent to AI models is limited to what is necessary to fulfill the specific AI feature request.</li>
                <li>We do not use your CRM data to train or fine-tune AI models.</li>
                <li>AI-generated outputs (summaries, suggestions, drafts) are stored within your CRM workspace and are subject to the same access controls as other CRM data.</li>
            </ul>

            <h3>2.3 Data Minimization</h3>
            <p>We follow data minimization principles when processing AI requests:</p>
            <ul>
                <li>Only relevant context is sent to AI models (not your entire database).</li>
                <li>Sensitive fields can be excluded from AI processing through organization settings.</li>
                <li>Conversations with the AI assistant are scoped to your organization's data only.</li>
            </ul>

            <h2>3. Third-Party AI Providers</h2>
            <p>We may use the following categories of third-party AI services:</p>
            <ul>
                <li><strong>Large Language Models (LLMs):</strong> For natural language understanding, text generation, summarization, and conversational AI features.</li>
                <li><strong>Email Intelligence:</strong> For email parsing, sentiment analysis, and communication insights.</li>
            </ul>
            <p>Our agreements with AI providers include provisions that:</p>
            <ul>
                <li>Prohibit the use of your data for model training</li>
                <li>Require data deletion after processing</li>
                <li>Mandate appropriate security measures</li>
                <li>Restrict data sharing with third parties</li>
            </ul>

            <h2>4. Human Oversight and Limitations</h2>

            <h3>4.1 AI Is Assistive, Not Autonomous</h3>
            <p>AI features in NurtureCRM.ai are designed to assist, not replace, human decision-making:</p>
            <ul>
                <li>AI-generated emails are always presented as drafts for your review before sending.</li>
                <li>AI suggestions and insights should be validated by users before acting on them.</li>
                <li>No automated actions are taken without user confirmation (e.g., sending emails, modifying deals).</li>
                <li>AI does not make binding business decisions on your behalf.</li>
            </ul>

            <h3>4.2 Accuracy and Limitations</h3>
            <p>AI outputs may contain inaccuracies. Users should:</p>
            <ul>
                <li>Review AI-generated content before use</li>
                <li>Verify factual claims and data references</li>
                <li>Not rely solely on AI for critical business decisions</li>
                <li>Report inaccurate or problematic outputs to our support team</li>
            </ul>

            <h2>5. Your Controls</h2>
            <p>You have the following controls over AI features:</p>
            <ul>
                <li><strong>Feature Toggle:</strong> Organization administrators can enable or disable specific AI features.</li>
                <li><strong>Plan-Based Access:</strong> AI features are available based on your subscription plan tier.</li>
                <li><strong>Data Scope:</strong> You control what data is entered into the CRM and therefore what data is available to AI features.</li>
                <li><strong>Opt Out:</strong> You may contact us to opt out of specific AI processing activities.</li>
            </ul>

            <h2>6. Responsible AI Principles</h2>
            <p>We are committed to responsible AI use and adhere to the following principles:</p>
            <ul>
                <li><strong>Transparency:</strong> We disclose when content is AI-generated and how AI features work.</li>
                <li><strong>Fairness:</strong> We monitor AI outputs for bias and take steps to mitigate discriminatory outcomes.</li>
                <li><strong>Privacy:</strong> We minimize data exposure to AI systems and protect user privacy.</li>
                <li><strong>Security:</strong> We implement security controls for AI data processing pipelines.</li>
                <li><strong>Accountability:</strong> We maintain human oversight over AI systems and take responsibility for their outputs.</li>
            </ul>

            <h2>7. Changes to AI Features</h2>
            <p>As AI technology evolves, we may introduce new AI features or modify existing ones. We will update this policy to reflect material changes and notify users through the Service.</p>

            <h2>8. Contact Us</h2>
            <p>For questions about our AI practices:</p>
            <ul>
                <li><strong>Email:</strong> <a href="mailto:support@aicoaches.com">support@aicoaches.com</a></li>
                <li><strong>Address:</strong> AiCoaches dot com LLC, 111 NE 1ST ST, 8TH FLOOR 89145, Miami, Florida, USA 33132</li>
            </ul>
        </LegalLayout>
    );
}
